import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronRight, ShoppingCart, BadgeCheck, Star, Minus, Plus, MapPin, Camera } from "lucide-react";
import { toast } from "sonner";
import client, { apiErr } from "@/lib/api";
import { CAT_ICON, catColor, fmt, tierFor, imgUrl } from "@/lib/catalog";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function Product() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add, count } = useCart();
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [err, setErr] = useState("");

  useEffect(() => {
    client.get(`/products/${id}`).then((r) => {
      setP(r.data);
      setQty(r.data.moq);
    }).catch(() => nav("/browse"));
  }, [id]);

  useEffect(() => {
    if (!p) return;
    if (qty < p.moq) setErr(`الكمية أقل من الحد الأدنى للطلب (${p.moq})`);
    else if (qty > p.stock) setErr(`الكمية أكبر من المخزون المتاح (${p.stock})`);
    else setErr("");
  }, [qty, p]);

  if (!p) return <div className="p-6 text-center" style={{ color: "var(--k-ink-soft)" }}>جارٍ التحميل…</div>;

  const Icon = CAT_ICON[p.cat] || CAT_ICON.food;
  const valid = qty >= p.moq && qty <= p.stock;
  const tier = valid ? tierFor(p, qty) : null;

  const addToCart = () => {
    if (!valid) return;
    add({ productId: p.id, name: p.name, cat: p.cat, qty, unitPrice: tier.price });
    toast.success("أُضيف إلى السلة");
  };

  const canEditImage = user && (user.role === "admin" || user.role === "merchant");
  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await client.put(`/products/${p.id}/image`, { image: up.data.url });
      setP((prev) => ({ ...prev, image: up.data.url }));
      toast.success("تم تحديث صورة المنتج");
    } catch (er) {
      toast.error(apiErr(er.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const heroSrc = imgUrl(p.image);

  return (
    <div dir="rtl">
      <div className="sticky top-0 z-20 flex items-center justify-between px-3 py-3" style={{ background: "var(--k-paper)" }}>
        <button data-testid="back-btn" onClick={() => nav(-1)} className="k-tap w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}><ChevronRight size={20} /></button>
        <button data-testid="pd-cart-btn" onClick={() => nav("/cart")} className="k-tap w-10 h-10 rounded-full flex items-center justify-center relative" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
          <ShoppingCart size={18} />
          {count > 0 && <span className="font-num absolute -top-1 -right-1 text-[10px] font-bold text-white flex items-center justify-center" style={{ minWidth: 17, height: 17, borderRadius: 9, background: "var(--k-ruby)", padding: "0 4px" }}>{count}</span>}
        </button>
      </div>

      <div className="relative flex items-center justify-center text-white overflow-hidden" style={{ height: 220, background: `linear-gradient(140deg, ${catColor(p.cat)}, ${catColor(p.cat)}bb)` }}>
        {heroSrc ? <img src={heroSrc} alt={p.name} className="w-full h-full object-cover" /> : <Icon size={54} strokeWidth={1.5} />}
        {canEditImage && (
          <>
            <input ref={fileRef} type="file" accept="image/*" onChange={onPickImage} className="hidden" data-testid="pd-image-input" />
            <button data-testid="pd-change-image-btn" disabled={uploading} onClick={() => fileRef.current?.click()} className="k-tap absolute bottom-3 left-3 flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-bold" style={{ background: "rgba(18,32,58,.85)", color: "#fff", backdropFilter: "blur(6px)" }}>
              <Camera size={15} /> {uploading ? "جارٍ الرفع…" : heroSrc ? "تغيير الصورة" : "إضافة صورة"}
            </button>
          </>
        )}
      </div>

      <div className="p-5">
        <p className="text-[11.5px] mb-1.5" style={{ color: "var(--k-ink-soft)" }}>{p.catName}</p>
        <h1 data-testid="pd-title" className="font-head text-[20px] font-extrabold leading-snug mb-2">{p.name}</h1>
        <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-[12.5px] mb-3.5" style={{ color: "var(--k-ink-soft)" }}>
          <span className="flex items-center gap-1"><BadgeCheck size={14} style={{ color: "var(--k-emerald)" }} /> {p.store}</span>
          <span className="flex items-center gap-1"><MapPin size={13} /> {p.city}</span>
          <span className="flex items-center gap-1"><Star size={13} fill="var(--k-gold)" strokeWidth={0} /> {p.rating}</span>
        </div>

        <div className="flex gap-2 mb-4">
          <span className="text-[11.5px] font-bold px-3 py-1.5 rounded-full" style={{ background: "var(--k-gold-tint)", color: "var(--k-gold-deep)" }}>الحد الأدنى {p.moq} قطعة</span>
          <span className="text-[11.5px] font-semibold px-3 py-1.5 rounded-full" style={{ background: "var(--k-surface)", color: "var(--k-ink-soft)", border: "1px solid var(--k-line)" }}>متوفّر {p.stock.toLocaleString("en-US")}</span>
        </div>

        <p className="text-[13px] leading-7 mb-4" style={{ color: "var(--k-ink-soft)" }}>{p.desc}</p>

        {/* tiers */}
        <div className="rounded-2xl overflow-hidden mb-5" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
          <div className="px-3.5 py-2 text-[11px] font-bold" style={{ background: "var(--k-surface)", color: "var(--k-ink-soft)", borderBottom: "1px solid var(--k-line)" }}>أسعار الجملة حسب الكمية</div>
          {p.tiers.map((t, i) => {
            const hot = valid && qty >= t.min && (t.max === null || qty <= t.max);
            const range = t.max ? `${t.min}–${t.max}` : `+${t.min}`;
            return (
              <div key={i} className="flex justify-between px-3.5 py-2.5 text-[13px]" style={{ background: hot ? "var(--k-gold-tint)" : "transparent", borderBottom: i < p.tiers.length - 1 ? "1px solid var(--k-line)" : "none" }}>
                <span style={{ color: "var(--k-ink-soft)" }}>{range} قطعة</span>
                <span className="font-num font-bold">{fmt(t.price)} د.ل</span>
              </div>
            );
          })}
        </div>

        {/* stepper */}
        <label className="text-[13px] font-bold block mb-2">الكمية المطلوبة</label>
        <div className="flex items-center gap-3">
          <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="k-tap w-11 h-11 rounded-xl text-lg font-bold flex items-center justify-center" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}><Minus size={18} /></button>
          <input data-testid="qty-input" type="number" value={qty} onChange={(e) => setQty(parseInt(e.target.value) || 0)} className="font-num flex-1 text-center text-[16px] font-bold rounded-xl py-2.5" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }} />
          <button data-testid="qty-plus" onClick={() => setQty((q) => q + 1)} className="k-tap w-11 h-11 rounded-xl text-lg font-bold flex items-center justify-center" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}><Plus size={18} /></button>
        </div>
        <p data-testid="qty-warn" className="text-[12px] mt-2 min-h-[16px]" style={{ color: "var(--k-ruby)" }}>{err}</p>

        <div className="flex justify-between items-center rounded-2xl px-4 py-3.5 mt-2 mb-3" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
          <span className="text-[12.5px]" style={{ color: "var(--k-ink-soft)" }}>الإجمالي</span>
          <span data-testid="pd-total" className="font-num text-[21px] font-bold">{tier ? `${fmt(tier.price * qty)} د.ل` : "—"}</span>
        </div>

        <button data-testid="add-to-cart-btn" disabled={!valid} onClick={addToCart} className="k-tap w-full py-3.5 rounded-2xl font-bold text-[14.5px] text-white" style={{ background: valid ? "var(--k-ink)" : "#B9B6AA", opacity: valid ? 1 : 0.7 }}>
          أضف إلى السلة
        </button>
      </div>
    </div>
  );
}
